import './Menu.css'

import {
  autoUpdate,
  flip,
  offset,
  Placement,
  size,
  useFloating,
  VirtualElement,
} from '@floating-ui/react-dom'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useClickAway } from '../hooks/useClickAway'
import { defaultOverlayRootName, getOverlayRootElement } from './OverlayRoot'
import { useStack } from '../hooks/useStack'
import { cx } from '../utilities/cx'
import { normalizeToEl, RefOrEl } from '../utilities/normalize-to-el'

const menuGapSize = 4

export type MenuProps = {
  className?: string
  targetRefOrEl?: RefOrEl<HTMLDivElement>
  targetPosition?: { x: number; y: number }
  target?: ReactNode
  placement?: Placement
  tabIndex?: number
  disabled?: boolean
  shouldClose?: boolean
  shouldOpen?: boolean
  matchTargetWidth?: boolean
  targetRef?: (el: HTMLDivElement | null) => void
  onOpen?: () => void
  onClose?: () => void
  onFocus?: () => void
  onBlur?: () => void
  onFinalPlacement?: (placement: Placement) => void
  overlayRootName?: string
  children?: ReactNode
}

export const Menu = (props: MenuProps) => {
  const {
    className,
    targetRefOrEl,
    targetPosition,
    target,
    placement = 'bottom',
    tabIndex,
    disabled,
    shouldClose,
    shouldOpen,
    matchTargetWidth = true,
    targetRef,
    onFocus,
    onBlur,
    onOpen,
    onClose,
    onFinalPlacement,
    overlayRootName = defaultOverlayRootName,
    children,
  } = props

  const menuRef = useRef<HTMLDivElement | null>(null)
  const lastTargetEl = useRef<HTMLDivElement | null>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const isActive = useStack('overlays', isOpen)

  const {
    refs,
    floatingStyles,
    placement: finalPlacement,
  } = useFloating<HTMLDivElement | VirtualElement>({
    whileElementsMounted: autoUpdate,
    placement,
    middleware: [
      offset(menuGapSize),
      flip(),
      size({
        apply({ availableHeight, rects, elements }) {
          elements.floating.style.maxHeight = `${availableHeight - menuGapSize * 2}px`
          if (matchTargetWidth) elements.floating.style.minWidth = `${rects.reference.width}px`
        },
      }),
    ],
  })

  useEffect(() => {
    if (isOpen) onFinalPlacement?.(finalPlacement)
  }, [isOpen, finalPlacement])

  useEffect(() => {
    if (shouldOpen && !isOpen) handleOpen()
  }, [isOpen, shouldOpen])

  useEffect(() => {
    if (shouldClose && isOpen) handleClose()
  }, [isOpen, shouldClose])

  useEffect(() => {
    if (disabled && isOpen) handleClose()
  }, [isOpen, disabled])

  useEffect(() => {
    const targetEl = normalizeToEl(targetRefOrEl)
    if (!targetEl) return
    setIsOpen(true)
    refs.setReference(targetEl)
  }, [targetRefOrEl])

  useEffect(() => {
    if (!targetPosition) return
    setIsOpen(true)
    refs.setReference({
      getBoundingClientRect: () => ({
        x: targetPosition.x,
        y: targetPosition.y,
        width: 0,
        height: 0,
        top: targetPosition.y,
        right: targetPosition.x,
        bottom: targetPosition.y,
        left: targetPosition.x,
      }),
    })
  }, [targetPosition])

  const handleMenuEl = (el: HTMLDivElement | null) => {
    menuRef.current = el
    refs.setFloating(el)
  }

  const handleTargetEl = (el: HTMLDivElement | null) => {
    if (el && lastTargetEl.current !== el) {
      lastTargetEl.current = el
      refs.setReference(el)
    }
    targetRef?.(el)
  }

  const handleOpen = () => {
    if (!!disabled || isOpen) return
    setIsOpen(true)
    onOpen?.()
  }

  const handleClose = () => {
    if (targetPosition || targetRefOrEl) return
    setIsClosing(true)
    setTimeout(() => {
      setIsClosing(false)
      setIsOpen(false)
      onClose?.()
    }, 200)
  }

  useClickAway(menuRef, () => {
    if (isActive) handleClose()
  })

  const menuClasses = cx('menu', className, {
    'menu--open': isOpen && !isClosing,
    'menu--closing': isClosing,
  })

  return (
    <>
      {target && (
        <div
          className="menu__target"
          tabIndex={!disabled ? (tabIndex ?? 0) : undefined}
          ref={handleTargetEl}
          onFocus={onFocus}
          onBlur={onBlur}
          onClick={handleOpen}
        >
          {target}
        </div>
      )}
      {isOpen &&
        createPortal(
          <div className={menuClasses} ref={handleMenuEl} style={floatingStyles}>
            {children}
          </div>,
          getOverlayRootElement(overlayRootName)!,
        )}
    </>
  )
}
